import { useTranslation } from "react-i18next";
import { IconCarrot } from "../../../../common/icons/IconCarrot/IconCarrot";
import { Requirement } from "../../../../services/BronzeGroupTracking/bronzeGroupTracking.types";
import LeafTag from "../../../../common/leaf/LeafTag/LeafTag";
import defaultContent from "../../../../content/bronzeGroupTracking";
import "./GraduatedTile.scss";
import { BRONZE_TAGS } from "../../../../reducers/model/bgtFilters";
import { storedLocale } from "../../../Authentication/util";
import { getDisplayName } from "../../../../common/util/displayName";
import { getLongPeriodFormat } from "../../../../common/util/period";
import { useAppSelector } from "../../../../store";
import maskAboNumber from "../../../../common/util/maskAboNumber";

type Props = {
  aboName: string;
  aboLocalName: string;
  displayName: string;
  isoLanguage: string;
  isoCountry: string;
  aboNumber: string;
  affNumber: string;
  incentiveTag: "Bronze Builder" | "Bronze Foundation" | string;
  requirements: Requirement[];
  graduated: boolean;
  graduationPeriod: string;
  restarted: boolean;
  viewState?: "desktop" | "mobile";
};

export const GraduatedTile = ({
  aboName,
  aboLocalName,
  displayName,
  isoLanguage,
  isoCountry,
  aboNumber,
  affNumber,
  incentiveTag,
  requirements,
  graduated,
  graduationPeriod,
  restarted,
  viewState = "desktop",
}: Props) => {
  const { t } = useTranslation(["bronzeGroupTracking"]);

  // Use backend-resolved displayName with translation
  const aboDisplayName = getDisplayName(displayName, t);

  const formatedGraduationPeriod = getLongPeriodFormat(graduationPeriod, storedLocale());

  //Loslite is not called in bgt api so to avoid adding latency
  //check displayName equals Confidential User. Display name is "Confidential User" for all locales
  const isMaskConfidentialAboNumber =
    (useAppSelector((s) => s.boot.configuration.actionReports.maskConfidentialAboNumber) ?? false) &&
    displayName === "Confidential User";

  const renderTag = () => {
    const bronzeBuilderContent = t("bronzeBuilder", defaultContent["bronzeBuilder"]);
    const bronzeFoundationContent = t("bronzeFoundation", defaultContent["bronzeFoundation"]);
    return (
      <>
        <LeafTag hideTag={false} variant={incentiveTag === BRONZE_TAGS.BRONZE_BUILDER ? "darkRed" : "lightRed"}>
          {`${incentiveTag === BRONZE_TAGS.BRONZE_BUILDER ? bronzeBuilderContent : bronzeFoundationContent}`}
        </LeafTag>
      </>
    );
  };

  const renderGraduatedTileMobile = () => {
    return (
      <div className="GraduatedTile aboTrackingTile__display--mobile">
        <div className="aboTrackingTile__group">
          <div className="GraduatedTile__cell GraduatedTile__cell--mobileStatus">
            <span>{`${t(`graduated`, defaultContent["graduated"])}`}</span>
            <span>{renderTag()}</span>
          </div>
          <div className="GraduatedTile__aboName">
            <p>{aboDisplayName}</p>
          </div>
          <div className="GraduatedTile__aboNum">
            <p>#{isMaskConfidentialAboNumber ? maskAboNumber(aboNumber) : aboNumber}</p>
          </div>
          <div className="GraduatedTile__graduatedIn">
            <p>{`${t(`graduatedIn`, defaultContent["graduatedIn"], { graduationDate: formatedGraduationPeriod })}`}</p>
          </div>
        </div>

        <div className="GraduatedTile__arrow">
          <IconCarrot orientation="right" color="black" />
        </div>
      </div>
    );
  };

  const renderGraduatedTileDesktop = () => {
    return (
      <div className="GraduatedTile GraduatedTile__display--desktop">
        <div className="GraduatedTile__aboNameNum GraduatedTile__cell GraduatedTile__cell--name">
          <span className="GraduatedTile__aboName">{aboDisplayName}</span>
          <span className="GraduatedTile__aboNum">
            #{isMaskConfidentialAboNumber ? maskAboNumber(aboNumber) : aboNumber}
          </span>
        </div>

        <div className="aboTrackingTile__infoTags GraduatedTile__cell GraduatedTile__cell--tags">{renderTag()}</div>

        <div className="GraduatedTile__cell GraduatedTile__cell--status">
          {`${t(`graduated`, defaultContent["graduated"])}`}
        </div>

        <div className="GraduatedTile__cell GraduatedTile__cell--graduationPeriod">{formatedGraduationPeriod}</div>

        <div className="GraduatedTile__arrow">
          <IconCarrot orientation="right" color="black" />
        </div>
      </div>
    );
  };

  return <>{viewState === "mobile" ? renderGraduatedTileMobile() : renderGraduatedTileDesktop()}</>;
};
